import React, { useMemo, useCallback } from 'react';
import { View, StyleSheet } from 'react-native';
import Text from 'design-system/Text';
import Logo from 'design-system/Logo';
import { Button } from 'design-system';
import Spacer from 'design-system/Spacer';
import BlurAmountView from 'design-system/BlurAmountView';
import useColors from 'hooks/useColors';
import { useAppSelector } from 'store/hooks';
import usePasscodeModal from 'hooks/usePasscodeModal';
import { capitalizeFirstCharacter, formatAmount } from 'utils/formatting';
import TapableText from './TapableText';
import { useStockMeta } from '../hooks';
import AboutInfoRow from './AboutInfoRow';
import { orderConfirmViewStrings } from './OrderConfirmView';
import { getLimitStopOrderExpiryText } from '../utils/format';
import { formatDateForLatestPrice, formatDateForLatestPriceTooltip, formatNumberOfShares, formatAmount as formatAmountInvest, } from '../utils';
import FXFeeRow from './FXFeeRow';
import RowAmount from './RowAmount';
const LimitStopOrderConfirmView = ({ symbol, amount, shares, assetId, execute, orderType, expiryDate, latestPrice, totalAmount, fxConversion, latestPriceTimeStamp, priceConvertedToUserCurrency, }) => {
    const colors = useColors();
    const [getPin] = usePasscodeModal();
    const stockCache = useStockMeta(assetId);
    const scrambled = useAppSelector((store) => store.utils.scrambled);
    const handleConfirm = useCallback(async (forcePinAuthentication) => {
        const biometryDescription = `You are adding ${orderType} order of ${formatAmount(amount.value, amount.currency, 2, false)} for $${symbol}`;
        try {
            const pin = await getPin({
                title: 'Enter Your Pin Code',
                biometryDescription,
                forcePinAuthentication: typeof forcePinAuthentication === 'boolean' ? forcePinAuthentication : false,
            });
            try {
                await execute({ pin, symbol, amount, shares, expiryDate, orderType });
            }
            catch (e) {
                // Other errors are already handled in execute
                if ('message' in e && e.message === 'Refresh token expired') {
                    handleConfirm(true);
                }
            }
        }
        catch {
            // use cancelled
        }
    }, [amount, execute, expiryDate, getPin, orderType, shares, symbol]);
    const amountText = useMemo(() => ({
        limitStopAmount: formatAmount(amount.value, amount.currency, 2),
    }), [amount.currency, amount.value]);
    const latestPriceTimeString = formatDateForLatestPrice(latestPriceTimeStamp);
    const latestPriceTimeStringForTooltip = formatDateForLatestPriceTooltip(latestPriceTimeStamp);
    const latestPriceInGBP = `(est. ${formatAmountInvest(priceConvertedToUserCurrency)})`;
    return (<View>
      <Spacer h={46}/>
      <View style={styles.row}>
        <Text Text-20 flex>
          Sell ${symbol}
        </Text>
        <Logo R44 uri={stockCache?.iconUrl}/>
      </View>
      <Spacer h={8}/>
      <AboutInfoRow title="Order type" info={`${capitalizeFirstCharacter(orderType)} sell`} noPaddingHorizontal/>
      <RowAmount est showCents noBottomLine noPaddingHorizontal amount={latestPrice} testID="rowLatestPrice" subtitleRight={latestPriceInGBP} title={orderConfirmViewStrings.latestPrice} subtitleLeft={`(as of ${latestPriceTimeString})`} tooltipTitle={orderConfirmViewStrings.latestPriceTooltipTitle} tooltipText={`${orderConfirmViewStrings.latestPriceTooltipText}${latestPriceTimeStringForTooltip}`}/>
      <AboutInfoRow noPaddingHorizontal info={amountText.limitStopAmount} title={`${capitalizeFirstCharacter(orderType)} price`}/>
      <AboutInfoRow title="No. of shares" info={formatNumberOfShares(shares, false, false, null)} noPaddingHorizontal/>
      {!!expiryDate && (<AboutInfoRow title="Expiry" info={getLimitStopOrderExpiryText(expiryDate)} noPaddingHorizontal/>)}
      <FXFeeRow fxConversion={fxConversion}/>
      <BlurAmountView enabled={scrambled}>
        <RowAmount est bold showCents noBottomLine noPaddingHorizontal amount={totalAmount} title={orderConfirmViewStrings.totalAmount} tooltipText={orderConfirmViewStrings.totalAmountTooltipText} tooltipTitle={orderConfirmViewStrings.totalAmountTooltipTitle}/>
      </BlurAmountView>
      <Spacer h={16}/>
      <TapableText pdf Gray centered TextThin-12 linkColor={colors.text.brand} links={orderConfirmViewStrings.links} text={orderConfirmViewStrings.conditions}/>
      <Spacer h={32}/>
      <Button title="Confirm" brand onPress={handleConfirm}/>
    </View>);
};
export default LimitStopOrderConfirmView;
const styles = StyleSheet.create({
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
});
